import {
  TeButton,
  TeButtonMinimal,
  TeContainer,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeIcon,
  TeTag,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import CompanyIcon from '../Company/CompanyIcon';
import { formatAddress } from '../../utils/formatAddress';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';

interface Props {
  candidate: MigrationCandidateBusinessPartnerBody;
  onReview: (candidate: MigrationCandidateBusinessPartnerBody) => void;
  onRemove: (candidate: MigrationCandidateBusinessPartnerBody) => void;
}

/**
 * Presentational row for a single Migration Candidate on the Pending merges
 * page. Renders the company icon, name, a source-system tag, the candidate's
 * formatted address, a Review action and an overflow menu with a single
 * "Remove suggestion" item.
 *
 * Owns no data fetching, cache, or modal state — the page owns those and wires
 * behaviour through the `onReview` / `onRemove` callbacks.
 */
function MigrationCandidateParentRow({ candidate, onReview, onRemove }: Props) {
  const { t } = useTranslation('companies', { keyPrefix: 'pendingMerges' });
  const address = formatAddress(candidate.address);

  return (
    <TeContainer
      alignItems="center"
      justifyContent="space-between"
      gap="xl"
      fill
      padding={['s', 'm']}
      backgroundColor="background.secondary"
      borderColor="border.secondary"
      borderRadius="l"
    >
      <TeContainerSlim gap="m" alignItems="flex-start" grow>
        <CompanyIcon size="l" />
        <TeContainerSlim column gap="8px" alignItems="flex-start" grow>
          <TeContainerSlim column gap="xs3" alignItems="flex-start">
            <TeText size={16} weight={500}>
              {candidate.name}
            </TeText>
            {candidate.sourceSystem && (
              <TeTag
                size="s"
                text={candidate.sourceSystem}
                backgroundColor="background.tertiary"
                textColor="text.primary"
                borderColor="border.primary"
              />
            )}
          </TeContainerSlim>
          {address && (
            <TeContainer
              gap="xs"
              alignItems="center"
              backgroundColor="background.primary"
              borderColor="border.secondary"
              borderRadius="l"
              padding="xs"
              height="auto"
            >
              <TeIcon name="domain" size="m" color="text.primary" />
              <TeText size={14} weight={400} color="text.primary">
                {address}
              </TeText>
            </TeContainer>
          )}
        </TeContainerSlim>
      </TeContainerSlim>

      <TeContainerSlim gap="s" alignItems="center">
        <TeButton
          variant="neutral"
          size="s"
          text={t('review')}
          teclick={() => onReview(candidate)}
        />
        <TeContextMenuControl contextId={`migration-candidate-${candidate.id}`}>
          <TeButtonMinimal variant="secondary" icon="more_vert" size="s" />
        </TeContextMenuControl>
        <TeContextMenu contextId={`migration-candidate-${candidate.id}`} size="s">
          <TeContextMenuItem text={t('removeSuggestion')} teclick={() => onRemove(candidate)} />
        </TeContextMenu>
      </TeContainerSlim>
    </TeContainer>
  );
}

export default MigrationCandidateParentRow;
export type { Props as MigrationCandidateParentRowProps };
